import { createContext, ReactNode, useCallback, useEffect, useReducer } from 'react';
// utils
import { setSession } from '../utils/jwt';
import { performLogout } from '../utils/auth';
import { setActingRoleInLocalStorage } from '../utils/actingRoleStore';
// @types
import { ActionMap, AuthState, JWTContextType } from '../@types/auth.types';
import { useLazyQuery } from "@apollo/client/react";
import { GET_USER } from '../graphql/resolvers/queries';
import { CoverageType, UserRole } from '../enums/shared.enums';
import { User } from '../@types/common.types';
import { GUEST_ROLE_NAME, PAYER_ROLES } from '../constants/common.constants';
import { Permission } from '../@types/shared.types';
import { ROLE_PERMISSION_MAP } from '../constants/shared.constants';
import useAnalytics from 'src/utils/useAnalytics';
import useLocales from '../hooks/useLocales';
import i18n from '../locales/i18n';

// ----------------------------------------------------------------------

enum Types {
    Login = 'LOGIN',
    Logout = 'LOGOUT',
    UpdateUser = 'UPDATE_USER',
}

type JWTAuthPayload = {
    [Types.Login]: {
        user: User;
    };
    [Types.Logout]: undefined;
    [Types.UpdateUser]: {
        user: User;
    };
};

export type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>];

const initialState: AuthState = {
    isAuthenticated: false,
    isInitialized: false,
    user: null,
};

const JWTReducer = (state: AuthState, action: JWTActions) => {
    switch (action.type) {
        case Types.Login:
            return {
                ...state,
                isAuthenticated: true,
                isInitialized: true,
                user: action.payload.user,
            };
        case Types.Logout:
            return {
                ...state,
                isAuthenticated: false,
                isInitialized: true,
                user: null,
            };
        case Types.UpdateUser:
            return {
                ...state,
                user: action.payload.user,
            };

        default:
            return state;
    }
};

const AuthContext = createContext<JWTContextType | null>(null);

// ----------------------------------------------------------------------

type AuthProviderProps = {
    children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
    // State
    const [state, dispatch] = useReducer(JWTReducer, initialState);

    const { registerUser, unregisterUser } = useAnalytics();

    const { changeLanguage } = useLocales();

    // Fetch user
    const [getUser, { data, error, refetch }] = useLazyQuery(GET_USER, {
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        if (data) {
            console.log('Logged in using token in local storage');

            const userLocale = data.user.language?.locale;
            const currentLocale = i18n.language;

            const applyLocale = async () => {
                if (userLocale && userLocale !== currentLocale) await changeLanguage(userLocale);
            };
            void applyLocale();

            registerUser(data.user);

            dispatch({
                type: Types.Login,
                payload: {
                    user: data.user,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (error) {
            console.log('Failed to login using token in local storage');
            dispatch({
                type: Types.Logout,
            });
            performLogout({ reload: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    const refetchUser = useCallback(async () => {
        const { data } = await refetch();

        dispatch({
            type: Types.UpdateUser,
            payload: {
                user: data.user,
            },
        });
    }, [refetch]);

    // Load access token and fetch user
    useEffect(() => {
        const accessToken = window.localStorage.getItem('accessToken');
        if (accessToken) {
            void getUser();
        } else {
            dispatch({
                type: Types.Logout,
            });
            performLogout({ reload: false });
        }
    }, []);

    // Login using token in local storage
    const login = useCallback(
        (accessToken: string, user: User): void => {
            setSession(accessToken);
            setActingRoleInLocalStorage();
            dispatch({
                type: Types.Login,
                payload: {
                    user,
                },
            });
            registerUser(user);
        },
        [registerUser]
    );

    // Logout
    const logout = useCallback((): void => {
        dispatch({ type: Types.Logout });
        performLogout({ unregisterAnalytics: unregisterUser, reload: false });
    }, [unregisterUser]);

    const hasPermission = useCallback(
        (permission: Permission): boolean => {
            const role = state.user?.role?.name;
            const allowedRoles = ROLE_PERMISSION_MAP[permission];

            return (
                role === UserRole.SUPER_ADMIN ||
                allowedRoles.includes('any') ||
                allowedRoles.includes(role as any)
            );
        },
        [state.user?.role?.name]
    );

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'jwt',
                login,
                logout,
                orgPreview: state.user?.organizationProfile?.organization,
                payer: state.user?.payerProfile?.payer,
                isAdmin: state.user?.role?.isAdmin || false,
                isSuperAdmin: state.user?.role?.name === UserRole.SUPER_ADMIN,
                isBillingAdmin: state.user?.role?.name === UserRole.BILLING_ADMIN,
                isClinicalSupervisor: state.user?.role?.name === UserRole.CLINICAL_SUPERVISOR,
                isRecruiter: state.user?.role?.name === UserRole.RECRUITER,
                isCredentialer: state.user?.role?.name === UserRole.CREDENTIALER,
                isSchedulingAdmin: state.user?.role?.name === UserRole.SCHEDULING_ADMIN,
                isCCManager: state.user?.role?.name === UserRole.CC_MANAGER,
                isCareCoordinator: state.user?.role?.name === UserRole.CARE_COORDINATOR,
                isSupportSpecialist: state.user?.role?.name === UserRole.SUPPORT_SPECIALIST,
                isOrganizationAdmin: state.user?.role?.name === UserRole.ORGANIZATION_ADMIN,
                // Presentation flag: true for every role rendered with the payer
                // experience, not just PayerAdmin. Authorization is unrelated and
                // lives in the backend guards.
                isPayerAdmin: PAYER_ROLES.includes(state.user?.role?.name as UserRole),
                isNetworkPartnerAdmin: state.user?.role?.name === UserRole.NETWORK_PARTNER_ADMIN,
                isClient: state.user?.role?.name === UserRole.CLIENT,
                isEAPClient: state.user?.clientProfile?.coverageType === CoverageType.EAP,
                isProvider: state.user?.role?.name === UserRole.PROVIDER,
                isMultiRole: Boolean(state.user?.isMultiRole),
                userRole: (state.user?.role?.name as any) || GUEST_ROLE_NAME,
                hasPermission,
                reload: refetchUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export { AuthContext, AuthProvider };
